import type { AggFunc, ColumnDef } from "../entities/types";
import type { AgGridColumnFilter } from "./ag-grid-filters";
import { translateAgGridFilterModel } from "./ag-grid-filters";
import { REPORT_MAX_ROWS } from "./chart-derive";
import {
  type BuildStructuredQueryInput,
  ExploreGridRequestError,
  type GridEntityDef,
  type GridValueCol,
  resolveGridSortModel,
} from "./grid-request";
import { measureAlias } from "./measure-alias";
import type {
  Filter,
  GroupBy,
  Measure,
  Sort,
  StructuredQueryConfig,
} from "./types";

export type BuildReportQueryInput = Omit<
  BuildStructuredQueryInput,
  "startRow" | "endRow" | "groupKeys"
>;

export type BuildSummaryQueryInput = {
  entity: GridEntityDef;
  groupBy: GroupBy[];
  measures: Measure[];
  filterModel?: Record<string, AgGridColumnFilter | null | undefined> | null;
  filters?: Filter[];
  dateRange?: StructuredQueryConfig["dateRange"];
  sort?: Sort[];
  limit?: number;
  offset?: number;
};

export function buildStructuredQueryForSummary(
  input: BuildSummaryQueryInput,
): StructuredQueryConfig {
  if (input.groupBy.length === 0) {
    throw new ExploreGridRequestError(
      "Summary mode requires a group-by column.",
    );
  }

  if (input.measures.length === 0) {
    throw new ExploreGridRequestError("Summary mode requires a metric.");
  }

  const columnMap = new Map(input.entity.columns.map((c) => [c.field, c]));
  const filters =
    input.filters ?? translateAgGridFilterModel(input.filterModel, columnMap);

  return {
    kind: "structured",
    entity: input.entity.name,
    columns: [],
    groupBy: input.groupBy,
    measures: input.measures,
    filters,
    dateRange: input.dateRange,
    sort: input.sort ?? defaultSummarySort(input.groupBy),
    limit: input.limit ?? REPORT_MAX_ROWS,
    offset: input.offset ?? 0,
  };
}

/** Summary charts need stable category order; compile defaults to measure DESC. */
function defaultSummarySort(groupBy: GroupBy[]): Sort[] {
  const firstGroup = groupBy[0];
  if (!firstGroup) {
    return [];
  }
  return [{ field: firstGroup.field, dir: "asc" }];
}

export function buildStructuredQueryForReport(
  input: BuildReportQueryInput,
): StructuredQueryConfig {
  if (
    input.pivotMode ||
    (input.pivotCols != null && input.pivotCols.length > 0)
  ) {
    throw new ExploreGridRequestError(
      "Pivot is not supported. Use Summary mode for charts and grouped data.",
    );
  }

  const columnMap = new Map(input.entity.columns.map((c) => [c.field, c]));
  const rowGroupCols = input.rowGroupCols;

  if (rowGroupCols.length === 0) {
    throw new ExploreGridRequestError(
      "Report mode requires at least one row group column.",
    );
  }

  const groupBy = rowGroupCols.map((col) => {
    const field = col.field ?? col.id ?? "";
    if (!field) {
      throw new ExploreGridRequestError("Row group column is missing a field.");
    }
    return { field };
  });

  const measures = buildReportMeasures(input.valueCols, columnMap);
  const gridFilters = translateAgGridFilterModel(input.filterModel, columnMap);
  const sort = resolveGridSortModel(
    input.sortModel,
    groupBy,
    measures,
    columnMap,
    [],
  );

  return {
    kind: "structured",
    entity: input.entity.name,
    columns: [],
    groupBy,
    measures,
    filters: gridFilters,
    dateRange: input.dateRange,
    sort,
    limit: REPORT_MAX_ROWS,
    offset: 0,
  };
}

function buildReportMeasures(
  valueCols: GridValueCol[],
  columnMap: Map<string, ColumnDef>,
): StructuredQueryConfig["measures"] {
  if (valueCols.length === 0) {
    return [{ field: "*", func: "count", alias: "ag_count" }];
  }

  return valueCols.map((col) => {
    const field = col.field ?? col.id ?? "*";
    const func = normalizeAggFunc(col.aggFunc);
    if (field !== "*" && !columnMap.has(field)) {
      throw new ExploreGridRequestError(`Unknown value column: ${field}`);
    }
    const measure = { field, func };
    return {
      field,
      func,
      alias: measureAlias(measure),
    };
  });
}

function normalizeAggFunc(aggFunc: string | null | undefined): AggFunc {
  if (aggFunc === "count_distinct" || aggFunc === "countDistinct") {
    return "count_distinct";
  }
  if (
    aggFunc === "count" ||
    aggFunc === "sum" ||
    aggFunc === "avg" ||
    aggFunc === "min" ||
    aggFunc === "max" ||
    aggFunc === "median" ||
    aggFunc === "p95"
  ) {
    return aggFunc;
  }
  return "count";
}

export function reportResultColumnFields(
  config: Pick<StructuredQueryConfig, "groupBy" | "measures">,
): string[] {
  const measureFields = config.measures.map((m) => measureAlias(m));
  const groupFields = config.groupBy.map((g) => g.field);
  return [...groupFields, ...measureFields];
}
